import React, { useState } from 'react';
import { styled } from '@mui/material';

import DownloadAllDocuments from 'parent-portal/provider-profile/tabs/documents/DownloadAllDocuments';
import DocumentLinks from 'parent-portal/provider-profile/tabs/documents/DocumentShared';
import { PreferWorkerModal } from 'parent-portal/shared/UserActions/WorkerLocationModal';
import { PromptForReviewModal } from 'parent-portal/shared/UserActions/PromptForReviewModal';
import useGoToProfile from '../goToProfile';

import { Avatar, Checkbox, Chip, ExpandableCard, IconButton, Link, PillButton, Row, Stack, Text } from 'library/ui';
import { Colors } from 'library';
import useClearinghouseVerification from 'parent-portal/shared/FloridaClearinghouse/useClearinghouseVerification';
import { dcfStatusLabel } from 'parent-portal/shared/FloridaClearinghouse/dcfStatuses';
import { PermissionType, useUserContext } from 'UserContext';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import type { BusinessLocationPreferredWorker } from 'models';
import type { IProviderSearch } from '../models';
import { PromptType } from 'parent-portal/shared/UserActions/PromptForReviewModal/PromptForReviewModal';

function formatLastShiftDate(dateString?: string | null): string | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const SPONSORSHIP_LABELS: Record<string, string> = {
    offered: 'Sponsorship offered',
    accepted: 'Sponsorship accepted — awaiting fingerprints',
    attended: 'Attended fingerprints — check their results',
    declined: 'Sponsorship declined',
};

const DocumentsIndent = styled('div')<{ $offset: number }>(({ $offset }) => ({
    paddingLeft: $offset,
}));

export function ProviderRow({
    provider,
    checked,
    handleCheck,
    onClearinghouseUpdated,
}: {
    provider: IProviderSearch;
    checked: boolean;
    handleCheck: (checked: boolean) => void;
    onClearinghouseUpdated?: () => void;
}) {
    const userAvatar = provider.profile_image?.image;

    const { user, hasPermission } = useUserContext();
    const { getProfilePath } = useGoToProfile();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const businessId = user?.businesses_active?.[0].id;

    const [openPreferredModal, setOpenPreferredModal] = useState<boolean>(false);
    const [openPromptForReviewModal, setOpenPromptForReviewModal] = useState<boolean>(false);
    const [promptType, setPromptType] = useState<PromptType>();

    const [preferredLocations, setPreferredLocations] = useState<number>(provider.preferred_workers?.length ?? 0);
    const [preferredAll, setPreferredAll] = useState<boolean>(
        !!provider.preferred_workers?.length && !provider.preferred_workers?.[0]?.business_location,
    );

    const isEligible = provider.hours_worked >= provider.total_trial_run_hours;
    const isPreferred = preferredAll || preferredLocations > 0;
    const isBlocked = (provider.business_outlaws?.length ?? 0) > 0;

    function handlePrefer(preferredWorkers: BusinessLocationPreferredWorker[], prompt: boolean = false) {
        const preferred: number = preferredWorkers.reduce(
            (total, preferredWorker) => total + (preferredWorker.is_preferred_worker ? 1 : 0),
            0,
        );
        const allPreferred = preferred === preferredWorkers.length;

        setPreferredAll(allPreferred);
        setPreferredLocations(preferred);
        setPromptType('prefer');
        setOpenPromptForReviewModal(prompt);
    }

    const preferredLabel = preferredAll
        ? 'all locations'
        : `${preferredLocations} location${preferredLocations !== 1 ? 's' : ''}`;
    const blockedLabel =
        !!provider.business_outlaws?.length && !provider.business_outlaws?.[0]?.business_location
            ? 'all locations'
            : `${provider.business_outlaws?.length ?? 0} location${(provider.business_outlaws?.length ?? 0) !== 1 ? 's' : ''}`;

    const lastShiftFormatted = formatLastShiftDate(provider.last_shift_date);
    const initials = `${provider.user.first_name?.[0] ?? ''}${provider.user.last_name?.[0] ?? ''}`;

    const pendingReview = provider.pending_clearinghouse_review;
    const [verifyError, setVerifyError] = useState<string>('');
    const { verificationModal, openVerification } = useClearinghouseVerification({
        workerId: provider.user.id,
        workerName: `${provider.user.first_name} ${provider.user.last_name}`,
        businessLocationId: pendingReview?.business_location,
        onProceed: () => onClearinghouseUpdated?.(),
        onReported: () => onClearinghouseUpdated?.(),
        onError: setVerifyError,
        submitLabel: 'Save',
        eligibleOutcomeCopy: `Once you confirm that ${provider.user.first_name} ${provider.user.last_name} is eligible, they can be booked automatically for future shifts. You can also request them to work at your center.`,
    });

    const detailParts = [
        `${provider.hours_worked.toFixed(1)} / ${provider.total_trial_run_hours} hours`,
        lastShiftFormatted ? `Last shift: ${lastShiftFormatted}` : null,
    ].filter(Boolean);

    return (
        <>
            <ExpandableCard
                header={
                    <Row justify="between" align="center" fullWidth>
                        <Row gap="sm" align="center">
                            {canPostJobs && (
                                <span onClick={stopPropagation}>
                                    <Checkbox checked={checked} onChange={handleCheck} />
                                </span>
                            )}
                            <Avatar imageUrl={userAvatar} initials={initials} size={48} />
                            <Stack gap="none">
                                <span onClick={stopPropagation}>
                                    <Link
                                        to={getProfilePath(provider.user.id)}
                                        weight="semibold"
                                        color={Colors.primary}
                                    >
                                        {provider.user.first_name} {provider.user.last_name}
                                    </Link>
                                </span>
                                <Text size="sm" tone="secondary">
                                    {detailParts.join(' · ')}
                                </Text>
                                {pendingReview && (
                                    <span onClick={stopPropagation}>
                                        <Row gap="sm" align="center" wrap>
                                            {!pendingReview.sponsorship ||
                                            pendingReview.sponsorship.status === 'declined' ? (
                                                <Chip
                                                    variant="filled"
                                                    bgColor={
                                                        pendingReview.eligibility_status === 'eligible'
                                                            ? Colors.secondarySoft
                                                            : Colors.warningBackground
                                                    }
                                                    textColor={
                                                        pendingReview.eligibility_status === 'eligible'
                                                            ? Colors.secondary
                                                            : Colors.warning
                                                    }
                                                >
                                                    {dcfStatusLabel(pendingReview.eligibility_status)}
                                                    {pendingReview.reported_at
                                                        ? ` — reported ${formatLastShiftDate(pendingReview.reported_at)}`
                                                        : ''}
                                                </Chip>
                                            ) : (
                                                <Chip
                                                    variant="filled"
                                                    bgColor={Colors.secondarySoft}
                                                    textColor={Colors.secondary}
                                                >
                                                    {SPONSORSHIP_LABELS[pendingReview.sponsorship.status]}
                                                </Chip>
                                            )}
                                            <PillButton variant="secondary" onClick={openVerification}>
                                                Update Clearinghouse Status
                                            </PillButton>
                                            {verifyError && (
                                                <Text size="sm" tone="error">
                                                    {verifyError}
                                                </Text>
                                            )}
                                        </Row>
                                    </span>
                                )}
                                {(isEligible || isPreferred || isBlocked) && (
                                    <Row gap="sm" wrap>
                                        {isEligible && (
                                            <Chip
                                                variant="filled"
                                                bgColor={Colors.secondarySoft}
                                                textColor={Colors.secondary}
                                            >
                                                Eligible for hire
                                            </Chip>
                                        )}
                                        {isPreferred && (
                                            <Chip
                                                variant="filled"
                                                bgColor={Colors.secondarySoft}
                                                textColor={Colors.secondary}
                                            >
                                                Preferred — {preferredLabel}
                                            </Chip>
                                        )}
                                        {isBlocked && (
                                            <Chip
                                                variant="filled"
                                                bgColor={Colors.errorBackground}
                                                textColor={Colors.error}
                                            >
                                                Blocked — {blockedLabel}
                                            </Chip>
                                        )}
                                    </Row>
                                )}
                            </Stack>
                        </Row>
                        <span onClick={stopPropagation}>
                            <Row gap="sm" align="center">
                                <IconButton
                                    icon={isPreferred ? StarIcon : StarBorderIcon}
                                    onClick={() => setOpenPreferredModal(true)}
                                    size={32}
                                    selected={isPreferred}
                                />
                                <DownloadAllDocuments provider={provider} />
                            </Row>
                        </span>
                    </Row>
                }
            >
                <DocumentsIndent $offset={canPostJobs ? 82 : 56}>
                    <DocumentLinks userId={provider.user.id} />
                </DocumentsIndent>
            </ExpandableCard>
            {businessId && (
                <PreferWorkerModal
                    open={openPreferredModal}
                    provider={{
                        id: provider.user.id,
                        first_name: provider.user.first_name,
                        last_name: provider.user.last_name,
                        profile_picture: { image: userAvatar ?? '' },
                    }}
                    onSuccess={handlePrefer}
                    onClose={() => setOpenPreferredModal(false)}
                />
            )}
            {verificationModal}
            {businessId && (
                <PromptForReviewModal
                    open={openPromptForReviewModal}
                    onClose={() => {
                        setOpenPromptForReviewModal(false);
                        setPromptType(undefined);
                    }}
                    userId={provider.user.id}
                    promptedByActionType={promptType}
                />
            )}
        </>
    );
}
